import ControllerBase from 'global/js/ControllerBase';
import { fromEvent } from 'rxjs';

interface ComponentRefs {
	/** The table outer wrapper, which has the which gradients (right and bottom) */
	table: 'table';
	/** The table wrapper, which has the overflow-x */
	tableInner: 'tableInner';
	/** The scroll left arrow */
	scrollLeft: 'scrollLeft';
	/** The scroll right arrow */
	scrollRight: 'scrollRight';
	/** The DIV which contains the show all button */
	showAllWrapper: 'showAllWrapper',
}

export default class ControllerProfessionalProductTableDesktop extends ControllerBase<ComponentRefs> {
	private table: HTMLDivElement;
	private tableInner: HTMLDivElement;
	private scrollLeft: HTMLAnchorElement;
	private scrollRight: HTMLAnchorElement;
	private showAllButton: HTMLAnchorElement | null;
	private hideAllButton: HTMLAnchorElement | null;
	/** A list of hidden table rows, if the table row limit has been set */
	private readonly hiddenTableRows: HTMLDivElement[];

	constructor(element: HTMLElement) {
		super(element);

		this.table = this.$references.table as HTMLDivElement;
		this.tableInner = this.$references.tableInner as HTMLDivElement;

		if (this.tableInner) {
			// set the initial number of hidden rows
			this.hiddenTableRows = Array.from(this.tableInner.querySelectorAll<HTMLDivElement>('div.js-col-hidden'));
		}

		this.init();
	}

	private init(): void {
		this.scrollLeft = this.$references.scrollLeft as HTMLAnchorElement;
		this.scrollRight = this.$references.scrollRight as HTMLAnchorElement;

		// handle toggle show/hide rows
		if (this.$references.showAllWrapper && this.hiddenTableRows.length > 0) {
			// get a ref to the button
			this.showAllButton = this.$references.showAllWrapper.querySelector<HTMLAnchorElement>(
				'a.a-button.m-professional-product-table-desktop__show'
			);
			this.showAllButton && this.showAllButton.addEventListener('click', (e) => this.handleShowAllToggle(e));
			this.hideAllButton = this.$references.showAllWrapper.querySelector<HTMLAnchorElement>(
				'a.a-button.m-professional-product-table-desktop__hide'
			);
			this.hideAllButton && this.hideAllButton.addEventListener('click', (e) => this.handleShowAllToggle(e));
		}

		this.scrollLeft && this.scrollLeft.addEventListener('click', (e) => this.handleTableScrolling(e, 'LEFT'));
		this.scrollRight && this.scrollRight.addEventListener('click', (e) => this.handleTableScrolling(e, 'RIGHT'));

		this.handleFirstLoadScrollArrows();
		this.observeTableScrolling();
		this.observeViewportResizing();
		this.setStickyColumn2Width();
	}

	private handleTableScrolling(event: MouseEvent, direction: 'LEFT' | 'RIGHT'): void {
		if (event) event.preventDefault();
		// if (!this.table) return;

		let scrollAmount = 270;

		switch (direction) {
			case 'LEFT': {
				scrollAmount = scrollAmount * -1;
				break;
			}

			default: break;
		}

		// this.tableWrapper.scrollLeft += scrollAmount;
		this.tableInner.scrollTo({
			left: this.tableInner.scrollLeft + scrollAmount,
			top: 0,
			behavior: 'smooth',
		});
	}

	/**
	 * Handle the disabled state of the arrow controls, depending on the scroll position of the table
	 * @returns void
	 */
	private observeTableScrolling(): void {
		this.tableInner.addEventListener('scroll', (ev: Event) => {
			// set the active attrib of the scroll button, based on where the user is along the x scroll axis
			const tableWidth = this.tableInner.scrollWidth;
			const tableDisplayWidth = this.table.scrollWidth;
			const scrollLeft = this.tableInner.scrollLeft;

			// viewport width including scrollbar
			// const viewportWidth = window.innerWidth
			// viewport width without scrollbar
			const viewportWidth = document.documentElement.clientWidth;

			switch (true) {
				case scrollLeft === 0: {
					// disable the left arrow, enable the right
					this.scrollLeft.setAttribute('disabled', 'true');
					this.scrollRight.removeAttribute('disabled');
					break;
				}

				case scrollLeft >= tableWidth - tableDisplayWidth: {
					// disable the right arrow, enable the left
					// @todo - disable the white gradient also
					this.scrollRight.setAttribute('disabled', 'true');
					this.scrollLeft.removeAttribute('disabled');
					break;
				}

				default: {
					// enable both arrows, if not already
					if (this.scrollLeft.hasAttribute('disabled')) {
						this.scrollLeft.removeAttribute('disabled');
					}

					if (this.scrollRight.hasAttribute('disabled')) {
						this.scrollRight.removeAttribute('disabled');
					}

					break;
				}
			}
		});
	}

	/**
	 * When a user clicks on the show all button, toggle shown/hidden table rows
	 * @param  {MouseEvent} event
	 * @returns void
	 */
	private handleShowAllToggle(event: MouseEvent): void {
		event.preventDefault();
		const SHOWN_CLASS = 'col--shown';
		const TRUNCATED_CLASS = 'truncated-y';
		// if rows have been shown then toggle should hide them, else show
		const shouldHide = this.hiddenTableRows.some(r => r.classList.contains(SHOWN_CLASS))
		this.hiddenTableRows.forEach(r => {
			shouldHide ? r.classList.remove(SHOWN_CLASS) : r.classList.add(SHOWN_CLASS);
		});

		// control the bottom white gradient, by adding/removing the class
		shouldHide ? this.table.classList.add(TRUNCATED_CLASS) : this.table.classList.remove(TRUNCATED_CLASS);

		// disable the show all button?
		// this.showAllButton?.setAttribute('disabled', 'true');

		//Toggle hide/show buttons when
		this.showAllButton?.classList.toggle("uk-hidden");
		this.hideAllButton?.classList.toggle("uk-hidden");
	}

	private handleFirstLoadScrollArrows(): void {
		const viewportWidth = document.documentElement.clientWidth;
		const tableWidth = this.tableInner.scrollWidth;

		if (tableWidth > viewportWidth) {
			this.scrollLeft.classList.remove('uk-hidden');
			this.scrollRight.classList.remove('uk-hidden');
		} else {
			this.scrollLeft.classList.add('uk-hidden');
			this.scrollRight.classList.add('uk-hidden');
		}
	}

	/**
	 * When the window is resized, decide if the table needs a right-vertical white gradient
	 * This should show when the width of the table is wider than the width of the viewport
	 * @returns void
	 */
	private observeViewportResizing(): void {

		this.table && window.addEventListener('resize', () => {
			const viewportWidth = document.documentElement.clientWidth;
			const tableWidth = this.tableInner.scrollWidth;
			const widthOfWhiteGradient = 100;
			// the class to add for the white gradient
			const truncatedX = 'truncated-x';

			if (tableWidth > viewportWidth + widthOfWhiteGradient) {
				this.scrollLeft.classList.remove('uk-hidden');
				this.scrollRight.classList.remove('uk-hidden');
				this.table.classList.contains(truncatedX) ? undefined : this.table.classList.add(truncatedX);

				this.setStickyColumn2Width();
			} else {
				this.scrollLeft.classList.add('uk-hidden');
				this.scrollRight.classList.add('uk-hidden');
				this.table.classList.contains(truncatedX) ? this.table.classList.remove(truncatedX) : undefined;
			}
		});
	}

	/**
	 * Set the second column's position when second column should be sticky also for a desktop table
	 * @returns void
	 */
	private setStickyColumn2Width(): void {
		const stickyColElementWidth = this.tableInner.querySelector<HTMLDivElement>('div.sticky-col-1')?.getBoundingClientRect().width;
		const stickyCol2Elements = this.tableInner.querySelectorAll<HTMLDivElement>('div.sticky-col-2');

		if (stickyColElementWidth && stickyCol2Elements && stickyCol2Elements.length > 0) {
			Array.from(stickyCol2Elements).forEach(element => {
				element.style.left = stickyColElementWidth + 'px';
			});
		}
	}
}
